import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useDeferredValue,
  useMemo,
} from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import moment from 'moment';
import 'moment/locale/pt-br';

import { getMoodColor } from './diaryStats';
import { computeAllInsights, countUniquePlaces } from './insights';
import { parseWorkspaceUrl } from './deepLinks';
import { buildTimelineHighlights } from './timelineHighlights';
import { buildCityReplayTimeline } from './cityReplay';
import {
  initialReplayExportState,
  resetReplayExportState,
} from './replayExportState';
import { PANEL_COLLAPSED, PANEL_NORMAL, PANEL_EXPANDED } from './calendarPanel';
import useFocusTrap from './useFocusTrap';
import { apiUrl, authFetch } from './config/apiClient';
import { suiteEventsToCalendarEvents } from './features/craftiaSuiteEvents';
import { useAuth } from './hooks/useAuth';
import { useMapData } from './hooks/useMapData';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { usePwaInstall } from './hooks/usePwaInstall';
import { useAtlasWorkspace } from './hooks/useAtlasWorkspace';
import { useMapLayers } from './hooks/useMapLayers';
import { useAppEventHandlers } from './hooks/useAppEventHandlers';
import AppMainView from './AppMainView';
import { ModalsProvider, useModals } from './ModalsProvider';
import { useToast } from './ToastProvider';
import {
  registerServiceWorker,
  activateWaitingServiceWorker,
} from './serviceWorkerRegistration';
import { detectTrips, detectHomeBase } from './tripDetector';
import { redactData, createEmptyPrivacyConfig } from './privacy';
import {
  NORMAL_ROUTE_STYLES,
  escapeHtml,
} from './MapRuntime';
import { buildShareableExport, downloadExport } from './shareExport';
import { buildAnniversaryItems } from './anniversaries';
import './PlaceTagsEditor.css';
import {
  EVENT_TYPES,
  NAVIGATION_PRESETS,
  applyFilterChange,
  FILTER_CHANGE_KINDS,
  createCalendarEventsFromRegistry,
  detectActiveNavigationPreset,
} from './features/registry';
import { isAtlasVivoEnabled } from './config/featureFlags';
import { buildPulseRailModel } from './features/pulseRail';
import { isDatasetEmpty } from './features/isDatasetEmpty';
import { generateNarrative } from './narrativeSummary';
import {
  getAppTarget,
  buildRouteSearchItems,
  filterRouteSearchItems,
  buildGlobalSearchItems,
  getGlobalSearchPreviewItems,
  getOutOfPeriodSearchItems,
  buildRevisitSuggestionItems,
  buildQualityHiddenRouteKeys,
  getCalendarDateRange,
  getTimelineHighlightsForDate,
} from './appHelpers';
import './App.css';
import { t } from './i18n';

moment.locale('pt-br');

export function getInitialPeriod(workspace = {}, now = moment()) {
  return {
    startDate: workspace.start || now.clone().startOf('month').format('YYYY-MM-DD'),
    endDate: workspace.end || now.format('YYYY-MM-DD'),
  };
}

function AppContent() {
  const { showToast } = useToast();
  const { user, authLoading, handleLoginSuccess, clearSession, logout } = useAuth();
  const isOnline = useOnlineStatus();
  const { canInstall, promptInstall } = usePwaInstall();
  const [pendingQualityIssue, setPendingQualityIssue] = useState(null);

  const [allTimeLightData, setAllTimeLightData] = useState({
    checkins: [], movies: [], shows: [], concerts: [], diary: [], spotify: [], appleMusic: [],
    music: null, mediaAggregates: false,
  });
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [suiteDayEvents, setSuiteDayEvents] = useState([]);
  const [currentFilters, setCurrentFilters] = useState(() => {
    const ws = parseWorkspaceUrl(typeof window !== 'undefined' ? window.location.search : '');
    return {
      ...getInitialPeriod(ws),
      selectedTypes: (ws.types && ws.types.length > 0)
        ? ws.types
        : EVENT_TYPES.map(et => et.key),
      focusedKey: ws.isolatedLayer || null,
    };
  });
  const {
    allFetchedData,
    dataQualityReport,
    loading,
    mapLoadPhase,
    fetchError,
    offlineCachedAt,
    fetchDataFromBackend,
    cancelMapFetch,
    retryFetch,
  } = useMapData({ user, clearSession, currentFilters });

  useEffect(() => {
    registerServiceWorker({
      onWaiting: (registration) => {
        showToast(t('web.update.ready'), 'info', {
          actionLabel: 'Recarregar',
          onAction: () => activateWaitingServiceWorker(registration),
        });
      },
    });
  }, [showToast]);
  const modalsState = useModals();
  const {
    isAnniversaryOpen,
    setIsAnniversaryOpen,
    isImportModalOpen,
    setIsImportModalOpen,
    pendingManualForm,
    setPendingManualForm,
    pendingImportFocus,
    setPendingImportFocus,
    isDiaryStatsOpen,
    setIsDiaryStatsOpen,
    isGithubOpen,
    setIsGithubOpen,
    isMemoryBookOpen,
    setIsMemoryBookOpen,
    isDashboardOpen,
    setIsDashboardOpen,
    selectedDiaryEntry,
    setSelectedDiaryEntry,
    selectedConcert,
    setSelectedConcert,
    selectedEntertainment,
    setSelectedEntertainment,
    selectedMusicDay,
    setSelectedMusicDay,
    selectedPlaceForTags,
    setSelectedPlaceForTags,
    selectedDwellBin,
    setSelectedDwellBin,
    selectedTrip,
    setSelectedTrip,
    printSummaryRequest,
    setPrintSummaryRequest,
    openImport,
    openQuickAdd,
  } = modalsState;

  const [preserveViewport, setPreserveViewport] = useState(false); // From original App.js
  const [clusterMarkers, setClusterMarkers] = useState(true);
  const [calendarDate, setCalendarDate] = useState(() => {
    const ws = parseWorkspaceUrl(typeof window !== 'undefined' ? window.location.search : '');
    if (ws.date) return moment(ws.date, 'YYYY-MM-DD').toDate();
    return moment().toDate();
  });
  const [calendarView, setCalendarView] = useState('month');
  const [routeSearchTerm, setRouteSearchTerm] = useState('');
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [routeFocusRequest, setRouteFocusRequest] = useState(null);
  const [tagFilter, setTagFilter] = useState('');
  const [tagFilterVenueIds, setTagFilterVenueIds] = useState(null);
  // Seleção contextual unificada do Atlas Vivo (fase 2.4). Só uma por vez.
  // Alimentada por cliques no mapa quando a flag `atlasVivo` está ligada;
  // renderizada no painel contextual do AtlasVivoLayout. Não afeta os layouts
  // antigos (que seguem usando popups do Leaflet).
  // Flag estável (lida da URL/localStorage uma vez por carga). Usada tanto na
  // escolha do layout quanto na fábrica de marcadores do mapa.
  const atlasVivoActive = isAtlasVivoEnabled();

  const selectCalendarDate = useCallback((date) => {
    if (!date) return;
    const nextDate = moment(date).toDate();
    setCalendarDate(nextDate);
    setCalendarView('month');
    setCurrentFilters(prev => ({
      ...prev,
      ...getCalendarDateRange(nextDate, 'month'),
    }));
  }, []);

  const selectCalendarDayOnly = useCallback((date) => {
    if (!date) return;
    setCalendarDate(moment(date).toDate());
  }, []);

  const handleQuickAdd = useCallback(() => {
    setPendingManualForm(true);
    if (!atlasVivoActive) {
      openImport(true);
    }
  }, [atlasVivoActive, openImport, setPendingManualForm]);

  const {
    workspaceView,
    setWorkspaceView,
    activeStoryKey,
    setActiveStoryKey,
    atlasSelection,
    setAtlasSelection,
    railHoverPosition,
    setRailHoverPosition,
    railFocusPosition,
    setRailFocusPosition,
    railWorkspaceRequest,
    setRailWorkspaceRequest,
    applyWorkspaceFromUrl,
    handleGoToImport: goToImportWorkspace,
  } = useAtlasWorkspace({
    atlasVivoActive,
    currentFilters,
    setCurrentFilters,
    calendarDate,
    selectCalendarDayOnly,
    selectCalendarDate,
    onQuickAdd: handleQuickAdd,
  });

  const handleGoToImport = useCallback((featureKey) => {
    if (typeof featureKey === 'string' && featureKey) {
      setPendingImportFocus(featureKey);
    }
    goToImportWorkspace();
  }, [goToImportWorkspace, setPendingImportFocus]);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [calendarPanelMode, setCalendarPanelMode] = useState(
    () => getAppTarget() === 'mobile' ? PANEL_COLLAPSED : PANEL_NORMAL
  );
  const closeExpandedCalendar = useCallback(() => {
    setCalendarPanelMode((mode) => mode === PANEL_EXPANDED ? PANEL_NORMAL : mode);
  }, []);
  const calendarDialogRef = useFocusTrap(calendarPanelMode === PANEL_EXPANDED, closeExpandedCalendar);

  // Arrastar o calendário expandido pelo cabeçalho.
  const handleCalendarDragMove = useCallback((event) => {
    const drag = calendarDragRef.current;
    if (!drag.dragging) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    setCalendarDragPos({ top: drag.startTop + deltaY, left: drag.startLeft + deltaX });
  }, []);

  const handleCalendarDragEnd = useCallback(() => {
    calendarDragRef.current.dragging = false;
    window.removeEventListener('pointermove', handleCalendarDragMove);
    window.removeEventListener('pointerup', handleCalendarDragEnd);
  }, [handleCalendarDragMove]);

  const handleCalendarDragStart = useCallback((event) => {
    if (calendarPanelMode !== PANEL_EXPANDED) return;
    if (event.target.closest('button')) return;
    const rect = calendarDialogRef.current?.getBoundingClientRect();
    if (!rect) return;
    calendarDragRef.current = {
      dragging: true,
      startX: event.clientX,
      startY: event.clientY,
      startTop: rect.top,
      startLeft: rect.left,
    };
    setCalendarDragPos({ top: rect.top, left: rect.left });
    window.addEventListener('pointermove', handleCalendarDragMove);
    window.addEventListener('pointerup', handleCalendarDragEnd);
  }, [calendarPanelMode, calendarDialogRef, handleCalendarDragMove, handleCalendarDragEnd]);

  // Reseta a posição arrastada quando o calendário fecha/recolhe, pra reabrir centralizado.
  useEffect(() => {
    if (calendarPanelMode !== PANEL_EXPANDED) setCalendarDragPos(null);
  }, [calendarPanelMode]);
  const [appTarget, setAppTarget] = useState(getAppTarget);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [globalSearchActiveIndex, setGlobalSearchActiveIndex] = useState(0);
  const [serverSearchHits, setServerSearchHits] = useState([]);
  const [searchPeriodSnapshot, setSearchPeriodSnapshot] = useState(null);
  const [hiddenQualityIssueIds, setHiddenQualityIssueIds] = useState([]);
  const [privacyConfig, setPrivacyConfig] = useState(createEmptyPrivacyConfig);
  const [cityReplay, setCityReplay] = useState(null);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(1);
  const [replayIndex, setReplayIndex] = useState(0);
  const [replayExportState, setReplayExportState] = useState(initialReplayExportState);
  const [replayExportError, setReplayExportError] = useState('');

  const [calendarDragPos, setCalendarDragPos] = useState(null);
  const calendarDragRef = useRef({ dragging: false, startX: 0, startY: 0, startTop: 0, startLeft: 0 });

  const [showDwellHeatmap, setShowDwellHeatmap] = useState(false);
  const selectedFilterTypes = currentFilters.selectedTypes;

  const safeFetchedData = useMemo(
    () => privacyConfig.enabled ? redactData(allFetchedData, privacyConfig) : allFetchedData,
    [allFetchedData, privacyConfig]
  );
  const datasetEmpty = useMemo(() => isDatasetEmpty(safeFetchedData), [safeFetchedData]);

  const routeItems = useMemo(
    () => buildRouteSearchItems(safeFetchedData, selectedFilterTypes),
    [safeFetchedData, selectedFilterTypes]
  );
  const hiddenQualityRouteKeys = useMemo(
    () => buildQualityHiddenRouteKeys(hiddenQualityIssueIds, dataQualityReport.issues),
    [hiddenQualityIssueIds, dataQualityReport.issues]
  );
  const visibleRouteItems = useMemo(
    () => routeItems.filter(routeItem => !hiddenQualityRouteKeys.has(routeItem.qualityRouteKey)),
    [routeItems, hiddenQualityRouteKeys]
  );
  const routeItemsById = useMemo(
    () => new Map(visibleRouteItems.map(routeItem => [routeItem.id, routeItem])),
    [visibleRouteItems]
  );
  const insights = useMemo(
    () => computeAllInsights(safeFetchedData),
    [safeFetchedData]
  );
  const statStripSummary = useMemo(() => {
    const distanceKm = Object.values(insights.distanceByType || {}).reduce((sum, km) => sum + km, 0);
    return {
      distanceKm,
      places: countUniquePlaces(safeFetchedData),
      streakDays: insights.streak?.longest || 0,
      shows: insights.concerts?.totalConcerts || 0,
    };
  }, [insights, safeFetchedData]);
  const allTimeAwareData = useMemo(() => ({
    ...safeFetchedData,
    checkins: allTimeLightData.checkins,
    movies: allTimeLightData.movies,
    shows: allTimeLightData.shows,
    concerts: allTimeLightData.concerts,
    diary: allTimeLightData.diary,
    spotify: allTimeLightData.spotify,
    appleMusic: allTimeLightData.appleMusic,
    music: allTimeLightData.music ?? safeFetchedData.music,
  }), [safeFetchedData, allTimeLightData]);
  // Period compare needs all-time light sources for range B; routes stay filter-scoped.
  const insightsLoadedData = useMemo(() => ({
    ...allTimeAwareData,
    health: safeFetchedData.health,
    moves: safeFetchedData.moves,
    googleMaps: safeFetchedData.googleMaps,
  }), [allTimeAwareData, safeFetchedData.health, safeFetchedData.moves, safeFetchedData.googleMaps]);
  const timelineHighlights = useMemo(
    () => buildTimelineHighlights({ data: allTimeAwareData, calendarEvents, githubDays: [] }),
    [allTimeAwareData, calendarEvents]
  );
  const trips = useMemo(
    () => detectTrips(safeFetchedData),
    [safeFetchedData]
  );
  const homeBase = useMemo(
    () => detectHomeBase(safeFetchedData),
    [safeFetchedData]
  );
  const deferredRouteSearchTerm = useDeferredValue(routeSearchTerm);
  const filteredRouteItems = useMemo(
    () => filterRouteSearchItems(visibleRouteItems, deferredRouteSearchTerm),
    [visibleRouteItems, deferredRouteSearchTerm]
  );
  const globalSearchItems = useMemo(
    () => buildGlobalSearchItems(allTimeAwareData, visibleRouteItems),
    [allTimeAwareData, visibleRouteItems]
  );
  const deferredGlobalSearchTerm = useDeferredValue(globalSearchTerm);
  const revisitSuggestionItems = useMemo(
    () => buildRevisitSuggestionItems(moment().format('YYYY-MM-DD'), safeFetchedData),
    [safeFetchedData]
  );
  const filteredGlobalSearchItems = useMemo(() => {
    const preview = getGlobalSearchPreviewItems(globalSearchItems, deferredGlobalSearchTerm);
    if (deferredGlobalSearchTerm) return preview;
    return [...revisitSuggestionItems.slice(0, 3), ...preview];
  }, [globalSearchItems, deferredGlobalSearchTerm, revisitSuggestionItems]);
  const outOfPeriodSearchItems = useMemo(() => {
    if (!deferredGlobalSearchTerm || deferredGlobalSearchTerm.trim().length < 2) return [];
    return getOutOfPeriodSearchItems(
      serverSearchHits,
      currentFilters.startDate,
      currentFilters.endDate,
      filteredGlobalSearchItems
    );
  }, [
    serverSearchHits,
    deferredGlobalSearchTerm,
    currentFilters.startDate,
    currentFilters.endDate,
    filteredGlobalSearchItems,
  ]);

  useEffect(() => {
    const term = (deferredGlobalSearchTerm || '').trim();
    if (!isGlobalSearchOpen || term.length < 2) {
      setServerSearchHits([]);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const resp = await authFetch(
          apiUrl(`/api/search?q=${encodeURIComponent(term)}`),
          { signal: controller.signal }
        );
        if (!resp.ok) {
          setServerSearchHits([]);
          return;
        }
        const data = await resp.json();
        setServerSearchHits(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error?.name !== 'AbortError') setServerSearchHits([]);
      }
    }, 200);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [deferredGlobalSearchTerm, isGlobalSearchOpen]);

  const selectedDayEvents = useMemo(
    () => {
      const dayKey = moment(calendarDate).format('YYYY-MM-DD');
      const fromRegistry = calendarEvents
        .filter(event => moment(event.start).isSame(calendarDate, 'day'));
      const fromSuite = suiteDayEvents.filter(
        (event) => moment(event.start).format('YYYY-MM-DD') === dayKey
      );
      return [...fromRegistry, ...fromSuite].sort(
        (a, b) => new Date(a.start) - new Date(b.start)
      );
    },
    [calendarEvents, calendarDate, suiteDayEvents]
  );

  useEffect(() => {
    if (!user) {
      setSuiteDayEvents([]);
      return undefined;
    }
    const day = moment(calendarDate).format('YYYY-MM-DD');
    const controller = new AbortController();
    (async () => {
      try {
        const resp = await authFetch(
          apiUrl(`/api/craftia/events?day=${encodeURIComponent(day)}&limit=100`),
          { signal: controller.signal }
        );
        if (!resp.ok) return;
        const body = await resp.json();
        setSuiteDayEvents(suiteEventsToCalendarEvents(body.events || []));
      } catch (err) {
        if (err?.name !== 'AbortError') {
          setSuiteDayEvents([]);
        }
      }
    })();
    return () => controller.abort();
  }, [user, calendarDate]);
  // Dias com evento no período (fase 3.4/6.3). Memoizado pra não recriar o Set
  // a cada render — só recalcula quando os eventos mudam e o Atlas está ligado.
  const atlasVivoEventDays = useMemo(() => {
    if (!atlasVivoActive) return null;
    const days = new Set(calendarEvents.map(event => moment(event.start).format('YYYY-MM-DD')));
    suiteDayEvents.forEach((event) => {
      days.add(moment(event.start).format('YYYY-MM-DD'));
    });
    return days;
  }, [atlasVivoActive, calendarEvents, suiteDayEvents]);

  // Modelo da Pulse Rail (fase 3.1). Alimentado pelos eventos do calendário no
  // período atual; consumido pelo AtlasVivoLayout. Só calcula quando o Atlas
  // Vivo está ligado, pra não pesar nos layouts antigos.
  const pulseRailModel = useMemo(() => {
    if (!atlasVivoActive) return null;
    const events = calendarEvents.map(event => ({
      date: event.start,
      type: event.resource?.type,
    }));
    return buildPulseRailModel(events, {
      start: currentFilters.startDate,
      end: currentFilters.endDate,
    });
  }, [atlasVivoActive, calendarEvents, currentFilters.startDate, currentFilters.endDate]);
  // Headline editorial do Atlas (fase 8 — docs/plans/overhaul-ux-atlas-vivo.md).
  // Reusa o mesmo generateNarrative do Dashboard de insights (Épico 20) com os
  // agregados já computados em `insights` — não recalcula nada novo, só muda
  // a forma de apresentação.
  const atlasHeadline = useMemo(() => {
    if (!atlasVivoActive) return null;
    const { startDate, endDate } = currentFilters;
    const days = startDate && endDate ? moment(endDate).diff(moment(startDate), 'days') + 1 : 0;
    const topCity = (insights.cities || [])[0]?.city;
    return {
      eyebrow: `${days} ${days === 1 ? 'dia' : 'dias'} · ${pulseRailModel ? pulseRailModel.total : 0} registros`,
      title: topCity ? `${topCity}, visto de cima.` : 'Seu percurso, visto de cima.',
      subtitle: generateNarrative({
        range: { startDate, endDate },
        cities: insights.cities || [],
        distanceByType: insights.distanceByType || {},
        music: insights.music || {},
        entertainment: insights.entertainment || {},
        concerts: insights.concerts || {},
        diary: safeFetchedData.diary || [],
      }),
    };
  }, [atlasVivoActive, currentFilters, insights, pulseRailModel, safeFetchedData.diary]);
  const selectedDayHighlights = useMemo(
    () => getTimelineHighlightsForDate(timelineHighlights, calendarDate),
    [timelineHighlights, calendarDate]
  );
  const anniversaryItems = useMemo(
    () => buildAnniversaryItems({
      selectedDate: calendarDate,
      data: allTimeAwareData,
      selectedTypes: selectedFilterTypes,
      routeItems: visibleRouteItems,
      getMoodColor,
      focusedKey: currentFilters.focusedKey,
    }),
    [calendarDate, allTimeAwareData, selectedFilterTypes, visibleRouteItems, currentFilters.focusedKey]
  );

  const handleAnniversaryDateChange = useCallback((deltaDays) => {
    setCalendarDate((prev) => moment(prev).add(deltaDays, 'day').toDate());
  }, []);
  const activeLegendItems = useMemo(() => {
    const labelFor = (typeKey) => EVENT_TYPES.find(type => type.key === typeKey)?.label || typeKey;
    const legendKeys = [
      'checkin',
      'walking',
      'running',
      'cycling',
      'transport',
      'car',
      'health',
      'google_maps',
      'concerts',
    ];

    return legendKeys
      .filter(typeKey => selectedFilterTypes.includes(typeKey))
      .map(typeKey => ({
        key: typeKey,
        label: labelFor(typeKey).replace(' (Foursquare)', '').replace(' (Apple Health)', ''),
        color: typeKey === 'checkin'
          ? '#3498db'
          : typeKey === 'concerts'
            ? '#1abc9c'
          : NORMAL_ROUTE_STYLES[typeKey]?.color || '#8b5cf6',
      }));
  }, [selectedFilterTypes]);
  const activeNavigationPresetKey = useMemo(
    () => detectActiveNavigationPreset(selectedFilterTypes),
    [selectedFilterTypes]
  );
  const activeNavigationPreset = useMemo(
    () => NAVIGATION_PRESETS.find(preset => preset.key === activeNavigationPresetKey) || null,
    [activeNavigationPresetKey]
  );
  const hasActivePresetResults = activeNavigationPreset
    ? loading || calendarEvents.length > 0 || routeItems.length > 0
    : true;

  const handleRouteSelect = useCallback((routeId) => {
    const routeItem = routeItemsById.get(routeId);
    setCurrentFilters(prevFilters => applyFilterChange(prevFilters, {
      kind: FILTER_CHANGE_KINDS.SET_FOCUSED_KEY,
      focusedKey: prevFilters.focusedKey && routeItem?.eventType !== prevFilters.focusedKey
        ? routeItem?.eventType || null
        : prevFilters.focusedKey || null,
    }));
    setSelectedRouteId(routeId);
    setRouteFocusRequest({ routeId, requestedAt: Date.now() });
  }, [routeItemsById]);

  const {
    mapRef,
    mapInitialized,
    focusRouteById,
    clearMapOnLogout,
    handleReplayRestart,
    handleReplayPlay,
    handleReplayPause,
    handleReplayExport,
  } = useMapLayers({
    user,
    preserveViewport,
    clusterMarkers,
    safeFetchedData,
    selectedFilterTypes,
    visibleRouteItems,
    routeItemsById,
    selectedRouteId,
    currentFilters,
    handleRouteSelect,
    tagFilterVenueIds,
    atlasVivoActive,
    setAtlasSelection,
    railHoverPosition,
    railFocusPosition,
    showDwellHeatmap,
    setSelectedDwellBin,
    cityReplay,
    setCityReplay,
    replayPlaying,
    setReplayPlaying,
    replaySpeed,
    setReplaySpeed,
    replayIndex,
    setReplayIndex,
    replayExportState,
    setReplayExportState,
    setReplayExportError,
    routeFocusRequest,
    appTarget,
    isMobileSidebarOpen,
    calendarPanelMode,
  });

  const handleLogout = useCallback(async () => {
    await logout({
      beforeUserClear: () => {
        clearMapOnLogout();
      },
    });
  }, [logout, clearMapOnLogout]);

  const handleTripSelect = useCallback((tripId) => {
    const trip = (trips.trips || []).find(t => t.id === tripId);
    if (!trip) return;

    if (selectedTripId === tripId) {
      setSelectedTrip(trip);
      return;
    }

    setSelectedTripId(tripId);

    setCurrentFilters(prevFilters => applyFilterChange(prevFilters, {
      kind: FILTER_CHANGE_KINDS.SET_DATE_RANGE,
      startDate: trip.startDate,
      endDate: trip.endDate,
    }));

    if (trip.bounds && mapRef.current) {
      const { minLat, maxLat, minLon, maxLon } = trip.bounds;
      const bounds = L.latLngBounds(
        L.latLng(minLat, minLon),
        L.latLng(maxLat, maxLon),
      );
      if (bounds.isValid()) {
        mapRef.current.flyToBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      }
    }
  }, [trips, selectedTripId]);

  const handleShareTrip = useCallback((trip) => {
    const json = buildShareableExport(safeFetchedData, trip, privacyConfig);
    const fileName = `viagem-${(trip.mainCity || 'desconhecida').toLowerCase().replace(/\s+/g, '-')}-${trip.startDate}.json`;
    downloadExport(json, fileName);
  }, [safeFetchedData, privacyConfig]);

  const handlePrintTrip = useCallback((trip) => {
    setPrintSummaryRequest({
      range: { startDate: trip.startDate, endDate: trip.endDate },
      title: trip.title || `Viagem · ${trip.mainCity || ''}`.trim(),
    });
  }, []);

  const handlePrintPeriodSummary = useCallback(() => {
    setPrintSummaryRequest({
      range: { startDate: currentFilters.startDate, endDate: currentFilters.endDate },
      title: t('web.print.periodSummary'),
    });
  }, [currentFilters.startDate, currentFilters.endDate]);

  const handleLegendLayerToggle = useCallback((layerKey) => {
    setCurrentFilters(prevFilters => applyFilterChange(prevFilters, ({
      kind: prevFilters.focusedKey === layerKey
        ? FILTER_CHANGE_KINDS.CLEAR_FOCUSED_KEY
        : FILTER_CHANGE_KINDS.SET_FOCUSED_KEY,
      focusedKey: prevFilters.focusedKey === layerKey ? undefined : layerKey,
    })));
  }, []);

  // Registros leves (sem GPS) buscados desde sempre, independente do período
  // selecionado — usado pra "cidade nova" e busca global não dependerem do filtro.
  const fetchAllTimeLightData = useCallback(async (signal) => {
    const params = new URLSearchParams();
    params.append('startDate', '2010-01-01');
    params.append('endDate', moment().format('YYYY-MM-DD'));
    ['checkin', 'movie', 'shows', 'concerts', 'diary', 'spotify', 'apple_music'].forEach(
      type => params.append('type', type)
    );
    try {
      const response = await authFetch(apiUrl(`/map-data?${params.toString()}`), { signal });
      if (!response.ok) {
        showToast(t('web.records.loadFail'));
        return;
      }
      const data = await response.json();
      setAllTimeLightData({
        checkins: data.checkins || [],
        movies: data.movies || [],
        shows: data.shows || [],
        concerts: data.concerts || [],
        diary: data.diary || [],
        spotify: data.spotify || [],
        appleMusic: data.appleMusic || [],
        music: data.music || null,
        mediaAggregates: Boolean(data.mediaAggregates),
      });
    } catch (error) {
      if (error.name === 'AbortError' || signal?.aborted) return;
      console.error('Error fetching all-time light data:', error);
      showToast(t('web.records.loadFail'));
    }
  }, [showToast]);

  useEffect(() => {
    if (user) {
      const controller = new AbortController();
      fetchAllTimeLightData(controller.signal);
      return () => controller.abort();
    }
  }, [user, fetchAllTimeLightData]);

  useEffect(() => {
    if (import.meta.env.VITE_TARGET) return undefined;

    const handleTargetResize = () => {
      setAppTarget(getAppTarget());
    };

    window.addEventListener('resize', handleTargetResize);
    return () => window.removeEventListener('resize', handleTargetResize);
  }, []);

  useEffect(() => {
    if (selectedRouteId && !routeItemsById.has(selectedRouteId)) {
      setSelectedRouteId(null);
      setRouteFocusRequest(null);
    }
  }, [routeItemsById, selectedRouteId]);

  useEffect(() => {
    if (currentFilters.focusedKey && !activeLegendItems.some(item => item.key === currentFilters.focusedKey)) {
      setCurrentFilters(prevFilters => applyFilterChange(prevFilters, {
        kind: FILTER_CHANGE_KINDS.CLEAR_FOCUSED_KEY,
      }));
    }
  }, [activeLegendItems, currentFilters.focusedKey]);

  // --- Data Processing for Calendar ---
  useEffect(() => {
    setCalendarEvents(createCalendarEventsFromRegistry({
      data: safeFetchedData,
      selectedTypes: selectedFilterTypes,
      routeItems: visibleRouteItems,
      getMoodColor,
      focusedKey: currentFilters.focusedKey,
    }));
  }, [safeFetchedData, selectedFilterTypes, visibleRouteItems, currentFilters.focusedKey]);

  const handleTagFilterApply = useCallback(async (tag) => {
    const trimmed = (tag || '').trim();
    setTagFilter(trimmed);
    if (!trimmed) {
      setTagFilterVenueIds(null);
      return;
    }
    const resp = await authFetch(apiUrl(`/api/tags/${encodeURIComponent(trimmed)}/places`));
    if (!resp.ok) {
      setTagFilterVenueIds(new Set());
      return;
    }
    const data = await resp.json();
    setTagFilterVenueIds(new Set((data.venueIds || []).map(String)));
  }, []);

  const handleTagFilterClear = useCallback(() => {
    setTagFilter('');
    setTagFilterVenueIds(null);
  }, []);

  useEffect(() => {
    const handlePopupClick = (event) => {
      const button = event.target.closest?.('.popup-edit-tags-btn');
      if (!button) return;
      const venueId = button.getAttribute('data-venue-id');
      const venueName = button.getAttribute('data-venue-name');
      if (venueId) setSelectedPlaceForTags({ id: venueId, name: venueName });
    };
    document.addEventListener('click', handlePopupClick);
    return () => document.removeEventListener('click', handlePopupClick);
  }, []);

  const searchVenues = useCallback(async (term) => {
    if (!term || term.length < 2) return [];
    const resp = await authFetch(apiUrl(`/api/places/search?q=${encodeURIComponent(term)}`));
    if (!resp.ok) return [];
    return resp.json();
  }, []);

  const searchCities = useCallback(async (term) => {
    if (!term || term.length < 2) return [];
    const resp = await authFetch(apiUrl(`/api/cities/search?q=${encodeURIComponent(term)}`));
    if (!resp.ok) return [];
    return resp.json();
  }, []);

  const handleCitySelect = useCallback((city) => {
    if (!mapRef.current || !city?.city) return;
    const cityCheckins = Array.isArray(allFetchedData.checkins)
      ? allFetchedData.checkins.filter(
          c => c.city && c.city.toLowerCase() === city.city.toLowerCase()
        )
      : [];
    if (cityCheckins.length > 0) {
      const bounds = L.latLngBounds(
        cityCheckins
          .filter(c => c.lat != null && c.lon != null)
          .map(c => [c.lat, c.lon])
      );
      if (bounds.isValid()) {
        mapRef.current.flyToBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      }
    }

    const timeline = buildCityReplayTimeline(
      allFetchedData,
      routeItems,
      city.city,
    );
    setReplayIndex(0);
    setReplayPlaying(false);
    setReplaySpeed(1);
    setReplayExportState(resetReplayExportState());
    setReplayExportError('');
    setCityReplay({ city: city.city, timeline });
  }, [allFetchedData, routeItems]);

  const handleVenueSelect = useCallback((venue) => {
    if (!mapRef.current || !venue?.lat || !venue?.lon) return;
    mapRef.current.flyTo([venue.lat, venue.lon], 17);
    L.popup({ closeOnClick: true, autoClose: true })
      .setLatLng([venue.lat, venue.lon])
      .setContent(`<b>${escapeHtml(venue.name)}</b>${venue.category ? `<br>${escapeHtml(venue.category)}` : ''}${venue.times_visited ? `<br>Visitado ${escapeHtml(venue.times_visited)}×` : ''}`)
      .openOn(mapRef.current);
  }, []);

  const {
    openGlobalSearch,
    closeGlobalSearch,
    handleFilterChange,
    handleNavigationPresetSelect,
    handlePresetWidenPeriod,
    handleToggleHiddenQualityIssue,
    handleApplyQualityCorrection,
    confirmQualityCorrection,
    handleCalendarNavigate,
    handleCalendarView,
    handleCalendarDaySelect,
    handleShowOnlyThisDay,
    handleCalendarSlotSelect,
    handleTimelineHighlightSelect,
    handleCalendarSelectEvent,
    handleGlobalSearchSelect,
    handleRestoreSearchPeriod,
    handleAnniversarySelect,
    handleDashboardCardAction,
    eventPropGetter,
    dayPropGetter,
  } = useAppEventHandlers({
    currentFilters,
    setCurrentFilters,
    setCalendarDate,
    setCalendarView,
    calendarView,
    calendarDate,
    setPendingQualityIssue,
    pendingQualityIssue,
    setHiddenQualityIssueIds,
    showToast,
    fetchDataFromBackend,
    handleRouteSelect,
    mapRef,
    setSelectedConcert,
    setSelectedDiaryEntry,
    setSelectedEntertainment,
    setSelectedMusicDay,
    setIsGithubOpen,
    setIsAnniversaryOpen,
    selectCalendarDate,
    setGlobalSearchTerm,
    setIsGlobalSearchOpen,
    setGlobalSearchActiveIndex,
    isGlobalSearchOpen,
    deferredGlobalSearchTerm,
    globalSearchItems,
    searchPeriodSnapshot,
    setSearchPeriodSnapshot,
    onGoToImport: handleGoToImport,
  });

  return (
    <AppMainView
      activeLegendItems={activeLegendItems}
      currentFilters={currentFilters}
      handleLegendLayerToggle={handleLegendLayerToggle}
      appTarget={appTarget}
      loading={loading}
      mapInitialized={mapInitialized}
      statStripSummary={statStripSummary}
      calendarPanelMode={calendarPanelMode}
      setCalendarPanelMode={setCalendarPanelMode}
      calendarDialogRef={calendarDialogRef}
      calendarDragPos={calendarDragPos}
      handleCalendarDragStart={handleCalendarDragStart}
      handleCalendarNavigate={handleCalendarNavigate}
      calendarDate={calendarDate}
      calendarView={calendarView}
      calendarEvents={calendarEvents}
      handleCalendarView={handleCalendarView}
      handleCalendarDaySelect={handleCalendarDaySelect}
      handleCalendarSlotSelect={handleCalendarSlotSelect}
      handleCalendarSelectEvent={handleCalendarSelectEvent}
      eventPropGetter={eventPropGetter}
      dayPropGetter={dayPropGetter}
      timelineHighlights={timelineHighlights}
      handleTimelineHighlightSelect={handleTimelineHighlightSelect}
      handleShowOnlyThisDay={handleShowOnlyThisDay}
      selectedDayHighlights={selectedDayHighlights}
      selectedDayEvents={selectedDayEvents}
      handleFilterChange={handleFilterChange}
      atlasVivoActive={atlasVivoActive}
      handleGoToImport={handleGoToImport}
      openImport={openImport}
      setIsAnniversaryOpen={setIsAnniversaryOpen}
      setIsDiaryStatsOpen={setIsDiaryStatsOpen}
      setIsDashboardOpen={setIsDashboardOpen}
      setIsGithubOpen={setIsGithubOpen}
      setIsMemoryBookOpen={setIsMemoryBookOpen}
      openGlobalSearch={openGlobalSearch}
      activeNavigationPresetKey={activeNavigationPresetKey}
      activeNavigationPreset={activeNavigationPreset}
      hasActivePresetResults={hasActivePresetResults}
      handleNavigationPresetSelect={handleNavigationPresetSelect}
      handlePresetWidenPeriod={handlePresetWidenPeriod}
      user={user}
      handleLogout={handleLogout}
      isDashboardOpen={isDashboardOpen}
      isImportModalOpen={isImportModalOpen}
      setIsImportModalOpen={setIsImportModalOpen}
      setIsFiltersCollapsed={setIsFiltersCollapsed}
      isFiltersCollapsed={isFiltersCollapsed}
      isFilterDrawerOpen={isFilterDrawerOpen}
      setIsFilterDrawerOpen={setIsFilterDrawerOpen}
      preserveViewport={preserveViewport}
      setPreserveViewport={setPreserveViewport}
      clusterMarkers={clusterMarkers}
      setClusterMarkers={setClusterMarkers}
      showDwellHeatmap={showDwellHeatmap}
      setShowDwellHeatmap={setShowDwellHeatmap}
      searchVenues={searchVenues}
      handleVenueSelect={handleVenueSelect}
      searchCities={searchCities}
      handleCitySelect={handleCitySelect}
      tagFilter={tagFilter}
      handleTagFilterApply={handleTagFilterApply}
      handleTagFilterClear={handleTagFilterClear}
      tagFilterVenueIds={tagFilterVenueIds}
      routeSearchTerm={routeSearchTerm}
      setRouteSearchTerm={setRouteSearchTerm}
      visibleRouteItems={visibleRouteItems}
      filteredRouteItems={filteredRouteItems}
      selectedRouteId={selectedRouteId}
      handleRouteSelect={handleRouteSelect}
      dataQualityReport={dataQualityReport}
      hiddenQualityIssueIds={hiddenQualityIssueIds}
      handleToggleHiddenQualityIssue={handleToggleHiddenQualityIssue}
      handleApplyQualityCorrection={handleApplyQualityCorrection}
      trips={trips}
      selectedTripId={selectedTripId}
      handleTripSelect={handleTripSelect}
      privacyConfig={privacyConfig}
      setPrivacyConfig={setPrivacyConfig}
      isGlobalSearchOpen={isGlobalSearchOpen}
      globalSearchTerm={globalSearchTerm}
      setGlobalSearchTerm={setGlobalSearchTerm}
      filteredGlobalSearchItems={filteredGlobalSearchItems}
      outOfPeriodSearchItems={outOfPeriodSearchItems}
      searchPeriodSnapshot={searchPeriodSnapshot}
      globalSearchItems={globalSearchItems}
      deferredGlobalSearchTerm={deferredGlobalSearchTerm}
      globalSearchActiveIndex={globalSearchActiveIndex}
      setGlobalSearchActiveIndex={setGlobalSearchActiveIndex}
      closeGlobalSearch={closeGlobalSearch}
      handleGlobalSearchSelect={handleGlobalSearchSelect}
      handleRestoreSearchPeriod={handleRestoreSearchPeriod}
      modalsState={modalsState}
      safeFetchedData={safeFetchedData}
      fetchDataFromBackend={fetchDataFromBackend}
      insights={insights}
      insightsLoadedData={insightsLoadedData}
      homeBase={homeBase}
      handleDashboardCardAction={handleDashboardCardAction}
      handlePrintPeriodSummary={handlePrintPeriodSummary}
      handleShareTrip={handleShareTrip}
      handlePrintTrip={handlePrintTrip}
      anniversaryItems={anniversaryItems}
      handleAnniversarySelect={handleAnniversarySelect}
      handleAnniversaryDateChange={handleAnniversaryDateChange}
      cityReplay={cityReplay}
      setCityReplay={setCityReplay}
      replayIndex={replayIndex}
      replayPlaying={replayPlaying}
      replaySpeed={replaySpeed}
      setReplaySpeed={setReplaySpeed}
      handleReplayPlay={handleReplayPlay}
      handleReplayPause={handleReplayPause}
      handleReplayRestart={handleReplayRestart}
      handleReplayExport={handleReplayExport}
      replayExportState={replayExportState}
      replayExportError={replayExportError}
      atlasSelection={atlasSelection}
      setAtlasSelection={setAtlasSelection}
      setSelectedPlaceForTags={setSelectedPlaceForTags}
      selectCalendarDayOnly={selectCalendarDayOnly}
      workspaceView={workspaceView}
      setWorkspaceView={setWorkspaceView}
      activeStoryKey={activeStoryKey}
      setActiveStoryKey={setActiveStoryKey}
      pendingManualForm={pendingManualForm}
      setPendingManualForm={setPendingManualForm}
      pendingImportFocus={pendingImportFocus}
      setPendingImportFocus={setPendingImportFocus}
      pulseRailModel={pulseRailModel}
      setRailHoverPosition={setRailHoverPosition}
      setRailFocusPosition={setRailFocusPosition}
      railWorkspaceRequest={railWorkspaceRequest}
      setRailWorkspaceRequest={setRailWorkspaceRequest}
      atlasVivoEventDays={atlasVivoEventDays}
      datasetEmpty={datasetEmpty}
      atlasHeadline={atlasHeadline}
      fetchError={fetchError}
      offlineCachedAt={offlineCachedAt}
      isOnline={isOnline}
      canInstall={canInstall}
      onInstallApp={promptInstall}
      retryFetch={retryFetch}
      isMobileSidebarOpen={isMobileSidebarOpen}
      setIsMobileSidebarOpen={setIsMobileSidebarOpen}
      authLoading={authLoading}
      handleLoginSuccess={handleLoginSuccess}
      mapLoadPhase={mapLoadPhase}
      cancelMapFetch={cancelMapFetch}
      pendingQualityIssue={pendingQualityIssue}
      confirmQualityCorrection={confirmQualityCorrection}
      setPendingQualityIssue={setPendingQualityIssue}
    />
  );
}

function AppRoot() {
  return (
    <ModalsProvider>
      <AppContent />
    </ModalsProvider>
  );
}

export default AppRoot;
